import { Link } from "react-router-dom";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { DEPARTMENTS, UG_BRANCH_COUNT } from "../lib/departments";
import {
  CURRICULUM_PILLARS,
  CREDIT_REQUIREMENTS,
  COURSE_CODING,
  CURRICULUM_PROCESS,
} from "../lib/curriculum";
import { useScrollPastHero } from "../lib/useScrollPastHero";

export default function Curriculum() {
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag="ACADEMICS · AUTONOMOUS"
        title="Curriculum & Syllabus"
        back={{ label: "Academics", to: ROUTES.academics }}
      />

      <section className="center-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.academics}>Academics</Link>
            <span aria-hidden>/</span>
            <span>Curriculum</span>
          </nav>
          <div className="ch-eyebrow">Autonomous since 2024</div>
          <span className="ch-short">FRAMEWORK</span>
          <h1 className="ch-name">Curriculum & Syllabus.</h1>
          <p className="ch-summary">
            BIET's autonomous curriculum is reviewed annually with industry partners, sequenced across four years, and structured to put rigor before application.
          </p>

          <div className="ch-stats">
            <div>
              <div className="num">180</div>
              <div className="lbl">Credits · B.E.</div>
            </div>
            <div>
              <div className="num">4</div>
              <div className="lbl">Pillars</div>
            </div>
            <div>
              <div className="num">{UG_BRANCH_COUNT}</div>
              <div className="lbl">UG branches</div>
            </div>
            <div>
              <div className="num">2024</div>
              <div className="lbl">Autonomous since</div>
            </div>
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              <h2>Four pillars</h2>
              <p>
                Every BIET undergraduate programme is organised around four pillars. The pillars determine sequencing and weight; the actual course list is decided by each branch's Board of Studies.
              </p>
              <ol className="event-schedule">
                {CURRICULUM_PILLARS.map((p, i) => (
                  <li key={i}>
                    <span className="es-time">{p.numeral}</span>
                    <span className="es-line" aria-hidden />
                    <span className="es-body">
                      <span className="es-what">{p.title}</span>
                      <span className="es-who">{p.body}</span>
                    </span>
                  </li>
                ))}
              </ol>

              <h2>Credit requirements (B.E.)</h2>
              <table className="center-projects">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th style={{ textAlign: "right" }}>Credits</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {CREDIT_REQUIREMENTS.map((r, i) => (
                    <tr key={i}>
                      <td className="tt">{r.category}</td>
                      <td className="amt" style={{ textAlign: "right" }}>{r.credits}</td>
                      <td>{r.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h2>Course coding</h2>
              <p>
                Course codes follow a three-letter-three-digit scheme. The letters indicate the offering branch (or HU/OE for general categories); the digits encode year and ordering within the year.
              </p>
              <table className="center-projects">
                <thead>
                  <tr><th>Code</th><th>Meaning</th></tr>
                </thead>
                <tbody>
                  {COURSE_CODING.map((c, i) => (
                    <tr key={i}>
                      <td className="amt">{c.code}</td>
                      <td>{c.meaning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h2>Curriculum review process</h2>
              <ol className="event-schedule">
                {CURRICULUM_PROCESS.map((p, i) => (
                  <li key={i}>
                    <span className="es-time">Step {i + 1}</span>
                    <span className="es-line" aria-hidden />
                    <span className="es-body"><span className="es-what">{p}</span></span>
                  </li>
                ))}
              </ol>

              <h2>Branch-specific syllabi</h2>
              <p>
                Detailed semester-wise course lists, electives and laboratory components live on each department's page. Click through for the branch you're interested in.
              </p>
              <div className="er-grid">
                {DEPARTMENTS.filter((d) => d.kind !== "science").map((d) => (
                  <Link key={d.slug} to={`${ROUTES.department}/${d.slug}`} className="er-card">
                    <div className="er-date">
                      <span className="er-day">{d.code}</span>
                      <span className="er-month">{d.level === "PG" ? "P.G." : "B.E."}</span>
                    </div>
                    <div className="er-body">
                      <span className="er-tag">{d.level === "PG" ? "Postgraduate" : "Undergraduate"}</span>
                      <h3>{d.name}</h3>
                      <span className="er-venue">{d.intro}</span>
                    </div>
                    <span className="er-arr" aria-hidden>→</span>
                  </Link>
                ))}
              </div>
            </div>

            <aside className="detail-meta-card is-accented">
              <div className="dmc-block">
                <div className="dmc-label">Status</div>
                <div className="dmc-value">Autonomous since 2024</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Total credits</div>
                <div className="dmc-value">180 (B.E.)</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Review cycle</div>
                <div className="dmc-value">Annual · May Board of Studies</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Authority</div>
                <div className="dmc-value">Academic Council</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Calendar</div>
                <div className="dmc-value">
                  <Link to={ROUTES.academicCalendar}>Academic Calendar →</Link>
                </div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Curriculum PDF</div>
                <div className="dmc-value">
                  <a href="#" onClick={(e) => e.preventDefault()}>Download per branch (coming soon)</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </article>
  );
}
